import { useEffect, useState } from 'react'
import * as Drawer from 'app/IntegrationDrawer'
import { format } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

import Alert from 'common/components/alert'
import Button from 'common/components/button'
import Loader from 'common/components/loaders/Loader'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'

import SelectComponent from '../components/select/SelectComponent'

import {
  fetchActivities,
  fetchTimezone,
  scheduleCampaign,
  setError,
  setLoading,
  setTimezone,
  updateActivities,
} from './store/adobeTargetSlice'
import DrawerContent from './DrawerContent'

export const initialValidationErrors: ValidationErrors = {
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  selectActivity: '',
  isOffersCreated: '',
  primaryGoal: '',
}

interface ValidationErrors {
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  selectActivity: string
  isOffersCreated: string
  primaryGoal: string
}

interface State {
  startDate: Date | undefined
  startTime: string | undefined
  endDate: Date | undefined
  endTime: string | undefined
  selectedActivity: number | undefined
  isOffersCreated: boolean
  isActivityScheduled: boolean
  errorMessages: ValidationErrors
  selectedPrimaryGoal: string | undefined
  showUnscheduleConfirmation: boolean
  showUnlinkConfirmation: boolean
  campaignUnscheduled: boolean
  campaignUnlinked: boolean
}

export const initialState: State = {
  startDate: undefined,
  startTime: undefined,
  endDate: undefined,
  endTime: undefined,
  selectedActivity: undefined,
  isOffersCreated: false,
  isActivityScheduled: false,
  selectedPrimaryGoal: undefined,
  showUnscheduleConfirmation: false,
  showUnlinkConfirmation: false,
  campaignUnscheduled: false,
  campaignUnlinked: false,
  errorMessages: {
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    selectActivity: '',
    isOffersCreated: '',
    primaryGoal: '',
  },
}

export default function AdobeTargetIntegration() {
  const dispatch = useAppDispatch()

  const [state, setState] = useState<State>(initialState)
  const {
    endTime,
    endDate,
    startDate,
    startTime,
    errorMessages,
    isOffersCreated,
    selectedActivity,
    isActivityScheduled,
    selectedPrimaryGoal,
    campaignUnscheduled,
    campaignUnlinked,
  } = state

  const { activitiesList, isLoading, timeZone, error } = useAppSelector(
    (state) => state.adobeTarget
  )

  const {
    campaignData: {
      project_id,
      _id: campaign_id,
      campaign_data: { integration_data },
    },
  } = useAppSelector((state) => state.campaignStates)

  useEffect(() => {
    if (!activitiesList && project_id && !campaignUnlinked) {
      dispatch(setLoading({ field: 'activities', value: true }))
      fetchActivities(project_id)
        .then((res) => {
          dispatch(updateActivities(res.data))
          dispatch(setLoading({ field: 'activities', value: false }))
        })
        .catch((err) => {
          dispatch(setLoading({ field: 'activities', value: false }))
          dispatch(setError(err.message))
        })
    }
    if (!timeZone) {
      fetchTimezone(project_id)
        .then((res) => {
          dispatch(setTimezone(res.data))
        })
        .catch((err) => {
          dispatch(setLoading({ field: 'activities', value: false }))
          dispatch(setError(err.message))
        })
    }
    if (
      !selectedActivity &&
      integration_data?.offers &&
      integration_data?.activityId &&
      !campaignUnlinked
    ) {
      setState({
        ...state,
        isOffersCreated: true,
        selectedActivity: integration_data.activityId,
      })
    }

    if (
      timeZone &&
      integration_data?.primaryGoal &&
      integration_data?.campaignTestDate &&
      integration_data?.campaignStartTestDate &&
      (!startDate || !startTime || !endDate || !endTime) &&
      !campaignUnscheduled
    ) {
      const startDateZoned = toZonedTime(
        new Date(integration_data.campaignStartTestDate),
        timeZone
      )
      const endDateZoned = toZonedTime(
        new Date(integration_data.campaignTestDate),
        timeZone
      )
      setState({
        ...state,
        isActivityScheduled: true,
        endDate: endDateZoned,
        endTime: format(endDateZoned, 'HH:mm'),
        startTime: format(startDateZoned, 'HH:mm'),
        startDate: startDateZoned,
        selectedPrimaryGoal: integration_data?.primaryGoal,
      })
    }
  }, [
    state,
    endDate,
    endTime,
    dispatch,
    timeZone,
    startDate,
    startTime,
    project_id,
    activitiesList,
    selectedActivity,
    campaignUnlinked,
    campaignUnscheduled,
    integration_data?.offers,
    integration_data?.activityId,
    integration_data?.primaryGoal,
    integration_data?.campaignTestDate,
    integration_data?.campaignStartTestDate,
  ])

  const onDateTimeChange = (val: Date | null | string, type: string): void => {
    setState({
      ...state,
      ...getChangedSchedulerValues({
        state,
        type,
        value: val,
      }),
    })
  }

  const validateInputs = (): boolean => {
    let isFormValid = true
    const validationErrors: ValidationErrors = initialValidationErrors
    // Validate that all required values exist
    if (!selectedActivity) {
      isFormValid = false
      validationErrors.selectActivity = 'Please select an Adobe Target activity'
    }
    if (!startDate || !startTime) {
      isFormValid = false
      validationErrors.startDate =
        'Experiment start date and time fields are required'
    }
    if (!endDate || !endTime) {
      isFormValid = false
      validationErrors.endDate =
        'Experiment end date and time fields are required'
    }
    if (!selectedPrimaryGoal) {
      isFormValid = false
      validationErrors.primaryGoal = 'Primary goal field is required'
    }
    if (!isOffersCreated) {
      isFormValid = false
      validationErrors.isOffersCreated = 'Offers have not been created'
    }
    setState({ ...state, errorMessages: validationErrors })
    return isFormValid
  }

  const onSchedule = () => {
    if (
      validateInputs() &&
      startDate &&
      startTime &&
      endDate &&
      endTime &&
      timeZone
    ) {
      const buildUTCString = (
        date: Date,
        timeStr: string,
        tz: string
      ): string => {
        const [h, m] = timeStr.split(':').map(Number)
        const localStr = `${format(date, 'yyyy-MM-dd')}T${String(h).padStart(
          2,
          '0'
        )}:${String(m).padStart(2, '0')}:00`
        const utcDate = fromZonedTime(localStr, tz)
        return format(utcDate, 'yyyy-MM-dd HH:mm')
      }
      const test_start_time = buildUTCString(startDate, startTime, timeZone)
      const test_end_time = buildUTCString(endDate, endTime, timeZone)
      const schedulePayload = {
        test_start_time,
        test_end_time,
        primary_goal: selectedPrimaryGoal,
      }
      dispatch(setLoading({ field: 'scheduler', value: true }))
      scheduleCampaign(project_id, campaign_id, schedulePayload)
        .then(() => {
          dispatch(setLoading({ field: 'scheduler', value: false }))
          setState({
            ...state,
            isActivityScheduled: true,
            errorMessages: initialValidationErrors,
            campaignUnscheduled: false,
          })
        })
        .catch((err) => {
          dispatch(setLoading({ field: 'scheduler', value: false }))
          dispatch(setError(err.message))
        })
    }
  }

  const getChangedSchedulerValues = ({
    state,
    type,
    value,
  }: {
    state: State
    type: string
    value: Date | null | string
  }) => {
    const { startDate, startTime, endDate, endTime } = state
    const setTime = (date: Date, timeStr: string): Date => {
      const [h, m] = timeStr.split(':').map(Number)
      const d = new Date(date)
      d.setHours(h, m, 0, 0)
      return d
    }
    switch (type) {
      case 'startDate': {
        const newStartDate = value ? (value as Date) : undefined
        return {
          startDate: newStartDate,
          endDate:
            newStartDate && endDate && !(newStartDate < endDate)
              ? new Date(newStartDate)
              : endDate,
          startTime: startTime && newStartDate ? startTime : undefined,
        }
      }
      case 'startTime': {
        const newStartTime = value ? (value as string) : undefined
        return {
          startDate:
            startDate && newStartTime
              ? setTime(startDate, newStartTime)
              : startDate,
          startTime: newStartTime,
          endTime:
            endTime && newStartTime && newStartTime > endTime
              ? undefined
              : endTime,
        }
      }
      case 'endDate': {
        const newEndDate = value ? (value as Date) : undefined
        return {
          endDate: newEndDate,
          endTime: endTime && newEndDate ? endTime : undefined,
        }
      }
      case 'endTime': {
        const newEndTime = value ? (value as string) : undefined
        return {
          endDate:
            endDate && newEndTime ? setTime(endDate, newEndTime) : endDate,
          endTime: newEndTime,
        }
      }
      default:
        return {}
    }
  }

  return (
    <>
      <Drawer.Content>
        <div className="updated-component">
          <Loader isLoading={Object.values(isLoading).includes(true)}>
            <div
              data-testid="adobeIntegrationTitle"
              className="text-base font-medium text-gray-400"
            >
              Connect your Adobe Target with your Jacquard experiment.
            </div>
            {isActivityScheduled && (
              <Alert type="success" className="flex items-center mt-6">
                This experiment is currently saved and collecting data from
                Adobe Target.
              </Alert>
            )}
            <SelectComponent
              className="mt-8"
              isLoading={isLoading.activities}
              selectOptions={activitiesList}
              isSelectDisabled={
                isLoading.activities || isActivityScheduled || isOffersCreated
              }
              dataTestId="selectActivity"
              onChange={(val) => setState({ ...state, selectedActivity: val })}
              selectValue={selectedActivity}
              labelTitle="Target Activity"
              bottomText={`This is a draft activity
             that has at least one configured experience.`}
              errorMessage={errorMessages.selectActivity}
              defaultOption="Select an unscheduled activity"
            />
            <DrawerContent
              onDateTimeChange={onDateTimeChange}
              campaignId={campaign_id}
              projectId={project_id}
              isLoading={isLoading}
              currentState={state}
              timeZone={timeZone}
              setState={setState}
            />
          </Loader>
        </div>
      </Drawer.Content>
      <Drawer.Footer error={error}>
        <Button
          loading={isLoading.scheduler}
          variant="primary"
          disabled={
            isActivityScheduled ||
            !selectedActivity ||
            !isOffersCreated ||
            !startDate ||
            !startTime ||
            !endDate ||
            !endTime
          }
          data-testid="scheduleBtn"
          onClick={onSchedule}
        >
          {isActivityScheduled ? 'Saved' : 'Save schedule'}
        </Button>
      </Drawer.Footer>
    </>
  )
}
